<template>
  <div class="col-lg-3 col-md-6 col-sm-6">
    <div :class="'card metric-card card-' + color">
      <div class="card-body">
        <template v-if="!status">
          <div class="d-flex justify-content-between align-items-start">
            <div class="metric-main w-75">
              <div class="skeleton sk-label-metric"></div>
              <div class="skeleton sk-value-metric mt-2"></div>
            </div>
            <div class="metric-icon" style="padding:0;">
              <div class="skeleton sk-icon-metric"></div>
            </div>
          </div>
          <div class="d-flex justify-content-end mt-3">
            <div class="skeleton sk-today-metric"></div>
          </div>
        </template>
        <template v-else>
          <div class="d-flex justify-content-between align-items-start">
            <div class="metric-main">
              <div class="metric-label-metric">{{ title }}</div>
              <div class="metric-value-metric">{{ formatNumber(totalGen) }}</div>
            </div>

            <div :class="'metric-icon icon-' + color">
              <i :class="icon"></i>
            </div>
          </div>

          <div class="d-flex justify-content-end" style="height: 25px;">
            <div v-bind:class="'metric-today-metric today-' + color" v-if="today">
              <i class="fas fa-calendar-day me-1"></i> today: {{ formatNumber(totalCurrent) }}
            </div>
          </div>
        </template>

      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  withDefaults(defineProps<{
    title: string
    totalGen: string
    totalCurrent: string
    icon: string
    color: string
    status: boolean
    today?: boolean
  }>(), {
    today: true
  })

  const formatNumber = (value: string) => {
    return Number(value).toLocaleString('en-US')
  }
</script>
<style scoped>
.metric-card-component {
  --metric-primary: #4e73df;
  --metric-primary-light: #e8eefd;
  --metric-success: #1cc88a;
  --metric-success-light: #e8f8f2;
  --metric-warning: #f6c23e;
  --metric-warning-light: #fef8e8;
  --metric-danger: #e74a3b;
  --metric-danger-light: #fce8e6;
  --metric-secondary: #858796;
  --metric-light: #f8f9fc;
  --metric-dark: #2e3a59;
  --metric-gray: #eaecf4;
  --metric-border: #e3e6f0;
}

.metric-card {
  border: none;
  border-radius: 16px;
  background: #fff;
  margin-bottom: 1.5rem;
  overflow: hidden;
  position: relative;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  transition: all .3s cubic-bezier(.4, 0, .2, 1);
}

.metric-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.1);
}

.metric-card::before {
  content: "";
  position: absolute;
  inset: 0 0 auto 0;
  height: 4px;
  opacity: 0;
  transition: opacity .3s;
}

.metric-card:hover::before {
  opacity: 1;
}

.card-process { border-top: 4px solid var(--metric-primary, #0cad4a); }
.card-pending { border-top: 4px solid var(--metric-primary, #4e73df); }
.card-failed  { border-top: 4px solid var(--metric-warning, #f6c23e); }
.card-error   { border-top: 4px solid var(--metric-danger, #e74a3b); }

.metric-icon,
.sk-icon-metric {
  width: 60px;
  height: 60px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
 font-size: 1.7rem;  
}

.icon-process { 
  background: var(--metric-primary-light, #a5f5af); 
  color: var(--metric-primary, #1cc88a); 
}

.icon-pending { 
  background: var(--metric-primary-light, #e8eefd); 
  color: var(--metric-primary, #4e73df); 
}
.icon-failed  { 
  background: var(--metric-warning-light, #fef8e8); 
  color: var(--metric-warning, #f6c23e); 
}
.icon-error   { 
  background: var(--metric-danger-light, #fce8e6); 
  color: var(--metric-danger, #e74a3b); 
}

.metric-label-metric {
  font-size: .85rem;
  font-weight: 600;
  letter-spacing: .5px;
  color: var(--metric-secondary, #858796);
}

.metric-value-metric {
  font-size: 2.2rem;
  font-weight: 800;
  color: var(--metric-dark, #2e3a59);
  line-height: 1;
  margin: .5rem 0;
}

.metric-today-metric {
  font-size: .9rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: .25rem;
  margin-left: auto;  
}

.today-pending { color: var(--metric-primary, #4e73df); }
.today-failed  { color: var(--metric-warning, #f6c23e); }
.today-error   { color: var(--metric-danger, #e74a3b); }


.skeleton {
  background: linear-gradient(90deg, #ececec 25%, #f4f4f4 50%, #ececec 75%);
  background-size: 200% 100%;
  animation: metric-shimmer 1.4s infinite;
  border-radius: 6px;
}

@keyframes metric-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}


.sk-label-metric  { width: 65%; height: 14px; }
.sk-value-metric  { width: 50%; height: 32px; border-radius: 8px; }
.sk-today-metric  { width: 40%; height: 16px; }
.sk-icon-metric   { border-radius: 12px; }


@media (max-width:768px) {
  .metric-value-metric { font-size: 1.8rem; }
}
</style>